import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import fieldConstants from '../constants/rmaFieldConstants.json';

const API_BASE = process.env.REACT_APP_API_URL;
const CLOSURE_REASONS = fieldConstants.prematureClosureReasons || [];

// Discouraged staff action: close a case before it completes the normal flow.
// Deliberately low-emphasis, and requires a confirmation + reason that is stored
// as learning data on the backend.
export default function CloseCaseAction({ rma, onSave }) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(CLOSURE_REASONS[0]?.value || 'other');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError('A reason is required to close a case prematurely.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ category, reason: reason.trim(), notes: notes.trim() })
      });
      if (!res.ok) {
        setError('Failed to close the case. Please try again.');
        setSaving(false);
        return;
      }
      const data = await res.json();
      onSave(data);
    } catch (err) {
      setError('Failed to close the case. Please try again.');
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          background: 'none',
          border: 'none',
          color: colors.textMuted,
          fontSize: '0.78rem',
          textDecoration: 'underline',
          cursor: 'pointer',
          padding: '0.25rem 0',
          opacity: 0.75
        }}
      >
        Close case prematurely
      </button>

      {open && (
        <div style={confirmStyles.overlay}>
          <div style={confirmStyles.dialog}>
            <h3 style={{ margin: '0 0 0.35rem', color: colors.danger }}>
              Close case prematurely?
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.88rem', color: colors.textBody }}>
              This ends the case outside the normal repair flow and should be the
              exception, not the rule. A reason is required and stored for review.
            </p>

            <label style={confirmStyles.label}>Reason category</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              style={confirmStyles.input}
            >
              {CLOSURE_REASONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            <label style={confirmStyles.label}>Reason (required)</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              placeholder="Explain why this case is being closed prematurely…"
              style={{ ...confirmStyles.input, resize: 'vertical' }}
            />

            <label style={confirmStyles.label}>Additional notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              style={{ ...confirmStyles.input, resize: 'vertical' }}
            />

            {error && (
              <p style={{ margin: '0 0 0.75rem', color: colors.danger, fontSize: '0.85rem' }}>
                {error}
              </p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => { setOpen(false); setError(''); }}
                disabled={saving}
                style={confirmStyles.cancelButton}
              >
                Keep case open
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving || !reason.trim()}
                style={{
                  ...confirmStyles.confirmButton,
                  opacity: saving || !reason.trim() ? 0.6 : 1,
                  cursor: saving || !reason.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                {saving ? 'Closing…' : 'Close case'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const confirmStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.overlay,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '1.25rem',
    backdropFilter: 'blur(12px)',
    zIndex: 1300
  },
  dialog: {
    background: 'linear-gradient(180deg, rgba(24,30,28,0.98), rgba(16,20,19,0.98))',
    padding: '1.4rem',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '440px',
    color: colors.textStrong,
    textAlign: 'left',
    border: `1px solid ${colors.danger}`,
    boxShadow: colors.shadow
  },
  label: {
    display: 'block',
    fontSize: '0.78rem',
    color: colors.textMuted,
    margin: '0 0 0.25rem'
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    marginBottom: '0.9rem',
    fontSize: '0.95rem',
    borderRadius: '10px',
    border: `1px solid ${colors.panelBorder}`,
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: colors.textStrong,
    boxSizing: 'border-box'
  },
  cancelButton: {
    background: colors.accent,
    color: '#11201c',
    border: 'none',
    padding: '0.6rem 1rem',
    fontSize: '0.9rem',
    cursor: 'pointer',
    borderRadius: '999px',
    fontWeight: 700
  },
  confirmButton: {
    background: 'rgba(255,255,255,0.06)',
    color: colors.danger,
    border: `1px solid ${colors.danger}`,
    padding: '0.6rem 1rem',
    fontSize: '0.9rem',
    borderRadius: '999px',
    fontWeight: 700
  }
};
